import { jsPDF } from 'jspdf';
import { pipeLatLngs, pathLength } from './model';
import { pipeSlope, coverOf } from './sewer';
import { clean, safeName } from './exporters';
import { companyLine } from './company';

const f2 = (v) => (v == null ? '–' : v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// chain of sewer sections downstream from a start section (end manhole -> next section starting there)
export function sewerChain(project, start) {
  const pipes = (project.pipes || []).filter((p) => p.type === 'sewer'), out = [start], seen = new Set([start.id]);
  for (let cur = start; cur;) {
    const end = cur.points[cur.points.length - 1]?.fid;
    cur = end && pipes.find((p) => !seen.has(p.id) && p.points[0]?.fid === end);
    if (cur) { out.push(cur); seen.add(cur.id); }
  }
  return out;
}

// Längsschnitt PDF: ground (cover) line, invert line, manholes, slope / DN / length per section
export function profilePdf(project, start, t) {
  const fits = project.fittings || [], chain = sewerChain(project, start), byId = Object.fromEntries(fits.map((f) => [f.id, f]));
  const st = [];
  let x = 0;
  chain.forEach((p, i) => {
    const s = pipeSlope(p, fits) || {}, L = pathLength(pipeLatLngs(p, fits)), a = byId[p.points[0]?.fid], b = byId[p.points[p.points.length - 1]?.fid];
    if (i === 0) st.push({ x, f: a, z: s.zA, g: coverOf(a) });
    x += L; st.push({ x, f: b, z: s.zB, g: coverOf(b), p, s, L });
  });
  const zs = st.flatMap((q) => [q.z, q.g]).filter((v) => v != null);
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' }), M = 48, W = 225, top = 40, H = 100;
  doc.setFillColor(8, 145, 178); doc.rect(0, 0, 297, 22, 'F'); doc.setTextColor(255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text(clean(`${t.lsTitle} – ${start.name}`), M, 10);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.text(clean([companyLine(), project.title, `${chain.length} ${t.lsSections} · ${f2(x)} m`].filter(Boolean).join(' · ')), M, 17);
  doc.setTextColor(20);
  if (!zs.length || x < 0.1) { doc.setFontSize(11); doc.text(clean(t.lsNoData), M, top + 10); doc.save(`${safeName(start.name)}_Laengsschnitt.pdf`); return; }
  const zMin = Math.min(...zs) - 0.5, zMax = Math.max(...zs) + 0.5;
  const X = (v) => M + (v / x) * W, Y = (z) => top + H - ((z - zMin) / (zMax - zMin)) * H;
  doc.setDrawColor(203, 213, 225); doc.setFontSize(7);
  for (let k = 0; k <= 5; k++) { const z = zMin + ((zMax - zMin) * k) / 5; doc.line(M, Y(z), M + W, Y(z)); doc.text(`${f2(z)}`, M - 8, Y(z) + 1, { align: 'right' }); }
  const line = (key, rgb, w) => { doc.setDrawColor(...rgb); doc.setLineWidth(w); for (let i = 1; i < st.length; i++) if (st[i - 1][key] != null && st[i][key] != null) doc.line(X(st[i - 1].x), Y(st[i - 1][key]), X(st[i].x), Y(st[i][key])); };
  line('g', [22, 163, 74], 0.5); line('z', [8, 145, 178], 0.9); doc.setLineWidth(0.2);
  st.forEach((q) => {
    doc.setDrawColor(71, 85, 105); if (q.g != null && q.z != null) doc.line(X(q.x), Y(q.g), X(q.x), Y(q.z));
    doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.text(clean(q.f?.name || '–'), X(q.x), top - 5, { align: 'center', maxWidth: 40 });
  });
  const rows = [[t.lsStation, (q) => f2(q.x)], [t.swCover, (q) => f2(q.g)], [t.swInvert, (q) => f2(q.z)]];
  let y = top + H + 10;
  rows.forEach(([lab, fn]) => { doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.text(clean(lab), M - 8, y, { align: 'right', maxWidth: 30 }); doc.setFont('helvetica', 'normal'); st.forEach((q) => doc.text(clean(fn(q)), X(q.x), y, { align: 'center' })); y += 6; });
  doc.setFont('helvetica', 'bold'); doc.text(clean(t.lsSection), M - 8, y, { align: 'right' }); doc.setFont('helvetica', 'normal');
  st.slice(1).forEach((q, i) => doc.text(clean(`${q.p.dn ? 'DN ' + q.p.dn + ' · ' : ''}L ${f2(q.L)} m · I ${q.s.pm != null ? q.s.pm.toFixed(1).replace('.', ',') : '–'} ‰`.replace('‰', 'o/oo')), (X(st[i].x) + X(q.x)) / 2, y, { align: 'center', maxWidth: Math.max(30, X(q.x) - X(st[i].x) - 2) }));
  doc.setFontSize(7); doc.setTextColor(100); doc.text(clean(t.lsLegend), M, 200);
  doc.save(`${safeName(start.name)}_Laengsschnitt.pdf`);
}
